import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

export enum TRACK_EVENTS {
  BUTTON_CLICK = "button_click"
}

export enum EVENT_CATEGORY {
  NOTIFICATIONS = "notifications",
  METRICS = "metrics",
  CHANNELS = "channels",
  BOTS = "bots",
  FLOWS = "flows",
  LIVECHAT = "livechat",
  TICKETS = "tickets",
  ROLES = "roles",
  ORGANIZATION = "organization",
  MEMBERS = "members",
  DASHBOARD = "dashboard",
  SIDEBAR = "sidebar",
  HEADER = "header",
  BREADCRUMB = "breadcrumb",
  AUTH = "auth",
  OTHER = "other"
}

export enum TRACK_LABEL {
  CREATE = "create",
  EDIT = "edit",
  DELETE = "delete",
  VIEW = "view",
  SAVE = "save",
  CANCEL = "cancel",
  CONNECT = "connect",
  DISCONNECT = "disconnect",
  INVITE = "invite",
  SEND = "send",
  CLOSE = "close",
  SUBMIT = "submit",
  RESET = "reset",
  RETRY = "retry",
  TOGGLE = "toggle",
  FILTER = "filter",
  SEARCH = "search",
  SIDEBAR = "sidebar",
  HEADER = "header",
  BREADCRUMB = "breadcrumb",
  MARK_ALL_READ = "mark_all_read",
  MARK_READ = "mark_read"
}

declare global {
  interface Window {
    gtag: (
      command: "config" | "event" | "js" | "set",
      targetId: string | Date,
      parameters?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

interface PageTrackingOptions {
  trackPageViews?: boolean;
  enableDebug?: boolean;
  customDimensions?: Record<string, string | number>;
}

interface EventData {
  category?: EVENT_CATEGORY;
  label?: TRACK_LABEL;
  // value?: number;
  // [key: string]: any;
}

const GA_TRACKING_ID = "G-QH0CZLDVF1";

export default function usePageTracking(options: PageTrackingOptions = {}) {
  const location = useLocation();
  const { trackPageViews = true, enableDebug = false, customDimensions } = options;

  const trackPageView = useCallback(
    (pagePath?: string) => {
      if (!window.gtag) {
        if (enableDebug) {
          console.warn("Google Analytics not loaded");
        }
        return;
      }

      const path = pagePath || location.pathname + location.search;

      window.gtag("config", GA_TRACKING_ID, {
        page_path: path,
        page_title: document.title,
        page_location: window.location.href,
        ...customDimensions
      });

      if (enableDebug) {
        console.log("Page view tracked:", path);
      }
    },
    [location, enableDebug, customDimensions]
  );

  const setUserId = useCallback(
    (userId: string) => {
      if (!window.gtag) {
        if (enableDebug) {
          console.warn("Google Analytics not loaded");
        }
        return;
      }

      window.gtag("config", GA_TRACKING_ID, {
        user_id: userId
      });

      if (enableDebug) {
        console.log("User ID set:", userId);
      }
    },
    [enableDebug]
  );

  const trackEvent = useCallback(
    (eventName: TRACK_EVENTS, eventData: EventData = {}) => {
      if (!window.gtag) {
        if (enableDebug) {
          console.warn("Google Analytics not loaded");
        }
        return;
      }

      window.gtag("event", eventName, {
        event_category: eventData.category || EVENT_CATEGORY.OTHER,
        event_label: eventData.label
      });

      if (enableDebug) {
        console.log("Event tracked:", eventName, eventData);
      }
    },
    [enableDebug]
  );

  const setUserProperties = useCallback(
    (properties: Record<string, string | number>) => {
      if (!window.gtag) {
        if (enableDebug) {
          console.warn("Google Analytics not loaded");
        }
        return;
      }

      window.gtag("set", "user_properties", properties);

      if (enableDebug) {
        console.log("User properties set:", properties);
      }
    },
    [enableDebug]
  );

  useEffect(() => {
    if (trackPageViews) {
      trackPageView();
    }
  }, [location, trackPageViews, trackPageView]);

  return {
    trackPageView,
    trackEvent,
    setUserId,
    setUserProperties
  };
}
